import { use, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { ChatSessionContext } from '../../contexts/ChatSessionContext'
import type { ChatMessage } from '../../types'
import { ChatBubble } from './ChatBubble'
import { useConversationStyles } from './ChatBubble.styles'
import { LoadingBubble } from './LoadingBubble'

const AUTO_SCROLL_BOTTOM_THRESHOLD_PX = 80

function isNearBottom(container: HTMLDivElement): boolean {
  return (
    container.scrollHeight - container.scrollTop - container.clientHeight <=
    AUTO_SCROLL_BOTTOM_THRESHOLD_PX
  )
}

interface ConversationProps {
  messages: ChatMessage[]
  isLoading?: boolean
  onCopy: (messageId: string) => void
  onEdit?: (messageId: string) => void
  onDelete?: (messageId: string) => void
  onInsert?: (messageId: string) => void
}

const Conversation = ({
  messages,
  isLoading = false,
  onCopy,
  onEdit,
  onDelete,
  onInsert,
}: ConversationProps) => {
  const chatSession = use(ChatSessionContext)
  const effectiveOnDelete = onDelete ?? chatSession?.deleteMessage

  const styles = useConversationStyles()
  const containerRef = useRef<HTMLDivElement>(null)
  const isFirstRun = useRef(true)
  const shouldAutoScrollRef = useRef(true)
  const hasStreamingAssistantMessage = useMemo(
    () => messages.some((m) => m.role === 'assistant' && m.streamState === 'streaming'),
    [messages]
  )

  // Runs before browser paint on mount — messages are already populated synchronously
  // so the user never sees the container scrolled to the top
  useLayoutEffect(() => {
    if (!containerRef.current) return
    containerRef.current.scrollTop = containerRef.current.scrollHeight
    shouldAutoScrollRef.current = true
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      shouldAutoScrollRef.current = isNearBottom(container)
    }

    handleScroll()
    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Smooth scroll when new messages arrive or loading state changes after mount
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }
    const container = containerRef.current
    if (!container || !shouldAutoScrollRef.current) return
    container.scrollTo({
      top: container.scrollHeight,
      behavior: hasStreamingAssistantMessage ? 'auto' : 'smooth',
    })
  }, [messages, isLoading, hasStreamingAssistantMessage])

  return (
    <div ref={containerRef} className={styles.container}>
      {messages.map((message) => (
        <div key={message.id}>
          <ChatBubble
            message={message}
            onCopy={onCopy}
            onEdit={onEdit}
            onDelete={effectiveOnDelete}
            onInsert={onInsert}
          />
          {message.infoNote && <div className={styles.infoNote}>{message.infoNote}</div>}
        </div>
      ))}
      {isLoading && !hasStreamingAssistantMessage && <LoadingBubble />}
    </div>
  )
}

export { Conversation }
